import { AuditLogCategory } from '@cdw-ae/common-data';
import type { SelectedFilterField } from '@cdw-ae/filter-bar-interfaces';
import {
  MatchersService,
  selectFromChipFormatter,
  selectFromToolTipFormatter,
} from '@cdw-ae/filter-bar-matcher-configs';
import {
  BATTERY_HEALTH_OPTIONS,
  DEVICE_STATUS_OPTIONS,
  DISK_USED_OPTIONS,
  multiContainsChipFormatter,
  multiContainsToolTipFormatter,
} from './filter-bar-config.helpers';

// Generate category options from enum to keep them in sync
const AUDIT_LOG_CATEGORY_OPTIONS = Object.values(AuditLogCategory).map(
  (category) => ({
    name: category,
    displayName: category,
  })
);

export const DeviceSearchFilterSettings = [
  {
    displayName: 'OU Path',
    name: 'vendorProperties.google.orgUnitPath',
    singleUse: true,
    sortPriority: 6,
    fieldMatchers: [
      MatchersService.Matchers.ouTreePicker,
    ],
  },
  {
    displayName: 'CDW Device ID',
    name: 'id',
    singleUse: true,
    sortPriority: 17,
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Device ID (Vendor)',
    name: 'vendorId',
    singleUse: true,
    sortPriority: 16,
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Asset ID',
    name: 'annotatedAssetId',
    singleUse: true,
    sortPriority: 2,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.multiTextSearch,
        suggestMatcher: true,
        options: {
          dialogWidth: '450px',
          chipFormatter: (value: string): string =>
            multiContainsChipFormatter(value),
          toolTipFormatter: (value: string): string =>
            multiContainsToolTipFormatter(value),
        },
      },
    ],
  },
  {
    displayName: 'Annotated User',
    name: 'annotatedUser',
    singleUse: true,
    sortPriority: 5,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.multiContains,
        suggestMatcher: true,
        options: {
          chipFormatter: (value: string): string =>
            multiContainsChipFormatter(value),
          toolTipFormatter: (value: string): string =>
            multiContainsToolTipFormatter(value),
        },
      },
    ],
  },
  {
    displayName: 'Annotated Location',
    name: 'annotatedLocation',
    singleUse: true,
    sortPriority: 7,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.multiContains,
        suggestMatcher: true,
        options: {
          chipFormatter: (value: string): string =>
            multiContainsChipFormatter(value),
          toolTipFormatter: (value: string): string =>
            multiContainsToolTipFormatter(value),
        },
      },
    ],
  },
  {
    displayName: 'Notes',
    name: 'notes',
    singleUse: true,
    sortPriority: 9,
    fieldMatchers: [
      { ...MatchersService.Matchers.contains, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Recent User',
    name: 'vendorProperties.google.lastKnownUser',
    singleUse: true,
    sortPriority: 4,
    fieldMatchers: [
      { ...MatchersService.Matchers.contains, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Device Status',
    name: 'status',
    singleUse: true,
    sortPriority: 3,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.selectFrom,
        editorOptions: {
          selectionOptions: DEVICE_STATUS_OPTIONS,
        },
        options: {
          chipFormatter: (value: string): string =>
            selectFromChipFormatter(value, DEVICE_STATUS_OPTIONS),
          toolTipFormatter: (value: string): string =>
            selectFromToolTipFormatter(value, DEVICE_STATUS_OPTIONS),
        },
      },
    ],
  },
  // EDUS-2618: there is deliberately NO separate "Make" filter. Make and Model are
  // two DB columns but one UI concept — the grid shows them concatenated, so the
  // Model filter below searches across both (saas-device-api maps `model` to the
  // composite `make+model` field). A standalone Make filter would just be a second
  // input for the same visible column.
  {
    displayName: 'Model',
    name: 'model',
    singleUse: true,
    sortPriority: 8,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.multiContains,
        suggestMatcher: true,
        options: {
          chipFormatter: (value: string): string =>
            multiContainsChipFormatter(value),
          toolTipFormatter: (value: string): string =>
            multiContainsToolTipFormatter(value),
        },
      },
    ],
  },
  {
    displayName: 'Serial Number',
    name: 'serialNumber',
    singleUse: true,
    sortPriority: 1,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.multiTextSearch,
        suggestMatcher: true,
        options: {
          dialogWidth: '450px',
          chipFormatter: (value: string): string =>
            multiContainsChipFormatter(value),
          toolTipFormatter: (value: string): string =>
            multiContainsToolTipFormatter(value),
        },
      },
    ],
  },
  {
    displayName: 'Last Sync',
    name: 'lastSync',
    singleUse: true,
    sortPriority: 10,
    fieldMatchers: [MatchersService.Matchers.dateTime],
  },
  {
    displayName: 'First Enrollment Time',
    name: 'vendorProperties.google.firstEnrollmentTime',
    singleUse: true,
    fieldMatchers: [MatchersService.Matchers.dateTime],
  },
  {
    displayName: 'Last Enrollment Time',
    name: 'vendorProperties.google.lastEnrollmentTime',
    singleUse: true,
    fieldMatchers: [MatchersService.Matchers.dateTime],
  },
  {
    displayName: 'Auto Update Expiry',
    name: 'vendorProperties.google.autoUpdateThrough',
    singleUse: true,
    sortPriority: 12,
    fieldMatchers: [
      { ...MatchersService.Matchers.dateTime, suggestMatcher: true },
      MatchersService.Matchers.is,
    ],
  },
  {
    displayName: 'Battery Health',
    name: 'batteryHealth',
    singleUse: true,
    sortPriority: 11,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.selectFrom,
        editorOptions: {
          selectionOptions: BATTERY_HEALTH_OPTIONS,
        },
        options: {
          chipFormatter: (value: string): string =>
            selectFromChipFormatter(value, BATTERY_HEALTH_OPTIONS),
          toolTipFormatter: (value: string): string =>
            selectFromToolTipFormatter(value, BATTERY_HEALTH_OPTIONS),
        },
      },
    ],
  },
  {
    displayName: 'Disk Used',
    name: 'vendorProperties.google.diskUsedCategory',
    singleUse: true,
    sortPriority: 15,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.selectFrom,
        editorOptions: {
          selectionOptions: DISK_USED_OPTIONS,
        },
        options: {
          chipFormatter: (value: string): string =>
            selectFromChipFormatter(value, DISK_USED_OPTIONS),
          toolTipFormatter: (value: string): string =>
            selectFromToolTipFormatter(value, DISK_USED_OPTIONS),
        },
      },
    ],
  },
  {
    displayName: 'OS Version Number',
    name: 'osVersion',
    singleUse: true,
    sortPriority: 14,
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'OS Version Status',
    name: 'osVersionStatus',
    singleUse: true,
    sortPriority: 13,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.oneOf,
        editorOptions: {
          selectionOptions: [
            { name: 'Latest OS', displayName: 'Latest OS' },
            { name: '1 OS Version Behind', displayName: '1 OS Version Behind' },
            {
              name: '2 OS Versions Behind',
              displayName: '2 OS Versions Behind',
            },
            {
              name: '3+ OS Versions Behind',
              displayName: '3+ OS Versions Behind',
            },
          ],
        },
      },
    ],
  },
];

export const UserCollectionsFilterSettings = [
  {
    displayName: 'User',
    name: 'user.name',
    fieldMatchers: [
      { ...MatchersService.Matchers.contains, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'User collection',
    name: 'name',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Description',
    name: 'description',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
];

export const DesignationsFilterSettings = [
  {
    displayName: 'User collection',
    name: 'userCollection.name',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'User',
    name: 'user.name',
    fieldMatchers: [
      { ...MatchersService.Matchers.contains, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'OU Collection',
    name: 'orgUnitCollection.name',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'OU Path',
    name: 'orgUnit.value',
    singleUse: true,
    fieldMatchers: [
      MatchersService.Matchers.ouTreePicker,
    ],
  },
  {
    displayName: 'Role',
    name: 'role.name',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
];

export const OuCollectionsFilterSettings = [
  {
    displayName: 'OU',
    name: 'orgUnit.value',
    fieldMatchers: [
      { ...MatchersService.Matchers.contains, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'OU collection',
    name: 'name',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Description',
    name: 'description',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
];

export const RolesFilterSettings = [
  {
    displayName: 'Role',
    name: 'name',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Description',
    name: 'description',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
];

export const AuditLogFilterSettings = [
  {
    displayName: 'Date and Time',
    name: 'created_at',
    fieldMatchers: [MatchersService.Matchers.dateTime],
  },
  {
    displayName: 'User',
    name: 'name',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Category',
    name: 'category',
    fieldMatchers: [
      {
        ...MatchersService.Matchers.selectFrom,
        editorOptions: {
          selectionOptions: AUDIT_LOG_CATEGORY_OPTIONS,
        },
        options: {
          chipFormatter: (value: string): string =>
            selectFromChipFormatter(value, AUDIT_LOG_CATEGORY_OPTIONS),
          toolTipFormatter: (value: string): string =>
            selectFromToolTipFormatter(value, AUDIT_LOG_CATEGORY_OPTIONS),
        },
      },
    ],
  },
  // Job Type (Standard/NA) retired from the audit log grid (EDUS-2952). The
  // backend still maps job_type to job_id IS [NOT] NULL, so uncommenting this
  // (plus the column + fieldMask entry in AuditLogComponent) restores it.
  // {
  //   displayName: 'Job Type',
  //   name: 'job_type',
  //   fieldMatchers: [
  //     {
  //       ...MatchersService.Matchers.oneOf,
  //       editorOptions: {
  //         selectionOptions: [
  //           { name: 'Standard', displayName: 'Standard' },
  //           { name: 'NA', displayName: 'NA' },
  //         ],
  //       },
  //     },
  //   ],
  // },
  {
    displayName: 'Action',
    name: 'action',
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
  {
    displayName: 'Job ID',
    name: 'job_id',
    hidden: true, // Hidden from UI but available for programmatic filtering
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
];

export const DevicesChromeProfileWipeSettings = [];

/**
 * State Analysis Report Filter Settings
 * These are the ONLY filters supported by the state-analysis API endpoint.
 * The state-analysis endpoint queries pre-aggregated data, not raw device data.
 *
 * Supported filters (map to DeviceStateAnalysisAsset entity columns):
 * - status → status
 * - orgUnitPath → groupingValue
 * - model → model
 * - batteryHealth → batteryHealth
 * - diskUsed → diskUsed
 * - osVersion → osVersion
 *
 * TODO: Add support for these state-analysis entity fields:
 * - deviceLastActiveTimeframe (needs new filter definition)
 *
 * TODO: (NOT supported by state-analysis):
 * - Make (make) — DeviceStateAnalysisAssetEntity has no `make` column; the pre-aggregated
 *   table groups by model/status/OU, not make. Adding make would require a new DB column,
 *   migration, and aggregation-pipeline changes. Track in a follow-up ticket.
 * - CDW Device ID (id)
 * - Device ID Vendor (vendorId)
 * - Asset ID (annotatedAssetId)
 * - Annotated User (annotatedUser)
 * - Annotated Location (annotatedLocation)
 * - Notes (notes)
 * - Recent User (recentUser)
 * - Serial Number (serialNumber)
 * - Last Sync (lastSync)
 * - OS Version Status (osVersionStatus)
 */
export const StateAnalysisReportFilterSettings = [
  {
    displayName: 'OU Path',
    name: 'vendorProperties.google.orgUnitPath',
    singleUse: true,
    fieldMatchers: [
      MatchersService.Matchers.ouTreePicker,
    ],
  },
  {
    displayName: 'Device Status',
    name: 'status',
    singleUse: true,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.selectFrom,
        editorOptions: {
          selectionOptions: DEVICE_STATUS_OPTIONS,
        },
        options: {
          chipFormatter: (value: string): string =>
            selectFromChipFormatter(value, DEVICE_STATUS_OPTIONS),
          toolTipFormatter: (value: string): string =>
            selectFromToolTipFormatter(value, DEVICE_STATUS_OPTIONS),
        },
      },
    ],
  },
  {
    displayName: 'Model',
    name: 'model',
    singleUse: true,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.multiContains,
        suggestMatcher: true,
        options: {
          chipFormatter: (value: string): string =>
            multiContainsChipFormatter(value),
          toolTipFormatter: (value: string): string =>
            multiContainsToolTipFormatter(value),
        },
      },
    ],
  },
  {
    displayName: 'Battery Health',
    name: 'batteryHealth',
    singleUse: true,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.selectFrom,
        editorOptions: {
          selectionOptions: BATTERY_HEALTH_OPTIONS,
        },
        options: {
          chipFormatter: (value: string): string =>
            selectFromChipFormatter(value, BATTERY_HEALTH_OPTIONS),
          toolTipFormatter: (value: string): string =>
            selectFromToolTipFormatter(value, BATTERY_HEALTH_OPTIONS),
        },
      },
    ],
  },
  {
    displayName: 'Disk Used',
    name: 'diskUsed',
    singleUse: true,
    fieldMatchers: [
      {
        ...MatchersService.Matchers.selectFrom,
        editorOptions: {
          selectionOptions: DISK_USED_OPTIONS,
        },
        options: {
          chipFormatter: (value: string): string =>
            selectFromChipFormatter(value, DISK_USED_OPTIONS),
          toolTipFormatter: (value: string): string =>
            selectFromToolTipFormatter(value, DISK_USED_OPTIONS),
        },
      },
    ],
  },
  {
    displayName: 'OS Version Number',
    name: 'osVersion',
    singleUse: true,
    fieldMatchers: [
      { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
      MatchersService.Matchers.fullMatch,
    ],
  },
];

export const STATE_ANALYSIS_SUPPORTED_FIELD_NAMES =
  StateAnalysisReportFilterSettings.map((f) => f.name);

export function filterToSupportedStateAnalysisFields(
  filters: SelectedFilterField[]
): SelectedFilterField[] {
  return filters.filter((f) =>
    STATE_ANALYSIS_SUPPORTED_FIELD_NAMES.includes(f.field.name)
  );
}
